import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from '../episode-selector.module.scss';
import clsx from 'clsx';

import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Button from '@smart-tv/components/button/button';

import { rootStore } from '../_stores/store-root';
import { ExtendedDescriptionDataProvider, uiStore } from '../_stores/store-ui';
import useNPVRSheet from '@smart-tv/hooks/use-npvr';
import { DeleteIcon } from '@smart-tv/components/icons/delete';
import { InfoOutlinedIcon } from '@smart-tv/components/icons/info-outlined';

interface EpisodeDescriptionReadMoreProps {
  className?: string;
  isFocusBoundary?: boolean;
  onArrowPress?: (direction: string) => boolean;
}

const EpisodeDescriptionReadMore = observer((props: EpisodeDescriptionReadMoreProps) => {
  const { className, isFocusBoundary = true, onArrowPress } = props;
  const { openDeleteRecordingDialog } = useNPVRSheet();

  const handleEnterPress = () => {
    uiStore.toggleExtendedEpisodeDescriptionDialog(true, ExtendedDescriptionDataProvider.EPISODE);
  };

  const { focusKey, ref } = useFocusable({
    onEnterPress: handleEnterPress,
    isFocusBoundary,
    focusBoundaryDirections: ['up', 'down', 'left', 'right']
  });

  const { episodes, focusedEpisode, nextEventForSeries } = rootStore.episodesStore;

  const episode =
    focusedEpisode.episodeId !== 0
      ? focusedEpisode.isNextEventforSeries
        ? nextEventForSeries?.episodeId === focusedEpisode.episodeId
          ? nextEventForSeries
          : null
        : episodes.find(
            (ep) =>
              ep.episodeId === focusedEpisode.episodeId &&
              ep.isNextEventforSeries === focusedEpisode.isNextEventforSeries
          )
      : null;

  const getKeyToFocusOn = uiStore.focusOnKey;

  const handleReadMoreFocus = () => {
    if (getKeyToFocusOn === 'focusBtnReadMore') {
      uiStore.setFocusOnKey('');
    }
  };

  if (!episode) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={clsx(styles['episode-detail__read-more'], className)} ref={ref}>
        <Button
          className={styles['episode-detail__read-more-button']}
          focusKey="focusBtnReadMore"
          isFocused={getKeyToFocusOn === 'focusBtnReadMore'}
          onClick={handleEnterPress}
          onArrowPress={onArrowPress}
          onNavFocus={handleReadMoreFocus}
          icon={<InfoOutlinedIcon />}
        >
          Info
        </Button>
        {episode.isContentRecorded && (
          <Button
            className={styles['episode-detail__delete-recording-button']}
            focusKey={`DELETE_RECORDING_${episode.episodeId}`}
            icon={<DeleteIcon />}
            onClick={() => {
              openDeleteRecordingDialog(episode);
            }}
          >
            Slet optagelse
          </Button>
        )}
      </div>
    </FocusContext.Provider>
  );
});

// display name
EpisodeDescriptionReadMore.displayName = 'EpisodeDescriptionReadMore';

export default EpisodeDescriptionReadMore;
